/**
 * Mechanical manuscript tidying, with no model involved.
 *
 * Both functions here are deliberately incapable of changing the author's prose: they decide where
 * paragraph breaks fall and how punctuation is spelled, and nothing else. That is what makes them
 * safe to run straight into the canvas on a button press, with no draft to review — a formatting
 * pass that could quietly reword a sentence would need one.
 *
 * The line they do not cross is inference. Splitting an unattributed exchange means guessing at
 * who is speaking, and a confident wrong guess costs the author more than leaving the block as
 * they typed it. Judgment calls stay with the AI rewrite in the selection menu.
 */

// Straight and curly, since a manuscript routinely contains both.
const OPENING_QUOTES = '"“'
const CLOSING_QUOTES = '"”'

// Verbs that mark the words around a speech as an attribution rather than narration. Deliberately
// a closed list: "she breathed" is an attribution, "she turned" is a stage direction, and only an
// enumerated set tells them apart without guessing.
const SPEECH_VERBS =
  'said|says|asked|asks|replied|replies|answered|answers|whispered|whispers|shouted|shouts|muttered|mutters|murmured|murmurs|added|adds|agreed|agrees|continued|continues|snapped|snaps|called|calls|breathed|breathes|admitted|admits|began|begins|offered|offers|remarked|remarks|repeated|repeats|insisted|insists|countered|counters|conceded|concedes|observed|observes|noted|notes'

const PRONOUNS = new Set(['he', 'she', 'they', 'i', 'we', 'you'])

interface Speech {
  /** Index of the opening quote mark. */
  start: number
  /** Index just past the closing quote mark. */
  end: number
  /** Lowercased name or pronoun the speech is attributed to, or null when unattributed. */
  speaker: string | null
}

/**
 * Whether two speakers are comparable enough to call a change of speaker.
 *
 * A name against another name, or a pronoun against another pronoun, is a real signal. A name
 * against a pronoun is not: "he" in the line after Oduya speaks may well be Oduya, and splitting
 * there would break up one character's turn.
 */
function isKnownSpeakerChange(previous: string | null, next: string | null): boolean {
  if (!previous || !next || previous === next) return false
  const previousIsPronoun = PRONOUNS.has(previous)
  const nextIsPronoun = PRONOUNS.has(next)
  return previousIsPronoun === nextIsPronoun
}

/**
 * Reads the attribution attached to a speech from the text immediately following it — "Ket said",
 * "said Ket", "he agreed". Returns null when the following text is narration rather than an
 * attribution, which is the common and important case: it is what keeps a beat like "She had
 * known this was coming" from being mistaken for a speaker.
 */
function readSpeaker(after: string): string | null {
  // Only the words directly after the closing quote can attribute it; anything further along
  // belongs to the next sentence.
  const window = after.slice(0, 60)
  const speakerFirst = window.match(
    new RegExp(`^[\\s,.;:—–-]*([A-Z][\\w'’-]*|he|she|they|I|we|you)\\s+(?:${SPEECH_VERBS})\\b`)
  )
  if (speakerFirst) return speakerFirst[1].toLowerCase()
  const verbFirst = window.match(
    new RegExp(`^[\\s,.;:—–-]*(?:${SPEECH_VERBS})\\s+([A-Z][\\w'’-]*)\\b`)
  )
  if (verbFirst) return verbFirst[1].toLowerCase()
  return null
}

/**
 * Finds the quoted speeches in a paragraph, in order, each with whoever it is attributed to.
 *
 * A quotation that carries no attribution and is not adjacent to one — a sign, a title, a quoted
 * phrase inside narration — simply comes back with a null speaker and can never trigger a split.
 */
function findSpeeches(text: string): Speech[] {
  const speeches: Speech[] = []
  let index = 0
  while (index < text.length) {
    const start = [...OPENING_QUOTES].reduce((earliest, mark) => {
      const found = text.indexOf(mark, index)
      return found !== -1 && (earliest === -1 || found < earliest) ? found : earliest
    }, -1)
    if (start === -1) break
    const closing = [...CLOSING_QUOTES].reduce((earliest, mark) => {
      const found = text.indexOf(mark, start + 1)
      return found !== -1 && (earliest === -1 || found < earliest) ? found : earliest
    }, -1)
    if (closing === -1) break
    const end = closing + 1
    speeches.push({ start, end, speaker: readSpeaker(text.slice(end)) })
    index = end
  }
  return speeches
}

/**
 * Character offsets in `text` where a new paragraph should begin.
 *
 * A break is planned only immediately before a speech whose speaker is known to differ from the
 * speaker of the speech before it. An unattributed speech inherits the last known speaker, so a
 * character's own follow-up line does not sever their turn; where no speaker can be read at all,
 * no break is planned.
 */
export function planParagraphSplits(text: string): number[] {
  const speeches = findSpeeches(text)
  if (speeches.length < 2) return []

  const splits: number[] = []
  let lastKnownSpeaker: string | null = speeches[0].speaker
  for (const speech of speeches.slice(1)) {
    if (isKnownSpeakerChange(lastKnownSpeaker, speech.speaker)) {
      // Break before the speech itself; whatever narration preceded it stays with the paragraph
      // it grew out of, which is the conservative placement when the beat could belong to either.
      splits.push(speech.start)
    }
    if (speech.speaker) lastKnownSpeaker = speech.speaker
  }
  return splits
}

/**
 * Position-independent punctuation and spacing fixes, safe to apply to a single text node.
 *
 * Nothing here trims the ends: a text node routinely sits next to inline markup, and stripping the
 * trailing space from "she said " would run it into the `<em>` that follows. Whole-paragraph
 * trimming is the caller's job, where the surrounding structure is known.
 */
export function normalizeTypography(text: string): string {
  return (
    text
      // Spaces and tabs only — collapsing newlines would join paragraphs in plain-text callers.
      .replace(/[ \t]{2,}/g, ' ')
      .replace(/(?<!-)--+(?!-)/g, '—')
      .replace(/\.{3,}/g, '…')
      .replace(/[ \t]+([,.;:!?])/g, '$1')
  )
}
